/* Hyperliquid public WebSocket helpers - all live data flows direct from HL to browser */

const WS_URL = "wss://api.hyperliquid.xyz/ws";

let socket = null;
const subscribers = new Map(); // key -> Set<callback>
let messageQueue = [];

function getKey(sub) {
    if (sub.type === "allMids") return "allMids";
    if (sub.type === "candle") return `candle:${sub.coin}:${sub.interval}`;
    return `${sub.type}:${sub.coin || ""}`;
}

function connect() {
    if (socket && socket.readyState <= 1) return socket;
    socket = new WebSocket(WS_URL);

    socket.onopen = () => {
        // resubscribe everything
        for (const key of subscribers.keys()) {
            const sub = parseKey(key);
            socket.send(JSON.stringify({ method: "subscribe", subscription: sub }));
        }
        // flush queued messages
        for (const m of messageQueue) socket.send(m);
        messageQueue = [];
    };

    socket.onmessage = (e) => {
        try {
            const msg = JSON.parse(e.data);
            const ch = msg.channel;
            const data = msg.data;
            let key = null;
            if (ch === "allMids") key = "allMids";
            else if (ch === "candle") {
                const c = data;
                key = `candle:${c.s}:${c.i}`;
            } else if (ch === "l2Book") {
                key = `l2Book:${data.coin}`;
            } else if (ch === "trades") {
                if (Array.isArray(data) && data.length) key = `trades:${data[0].coin}`;
            } else if (ch === "activeAssetCtx" || ch === "webData2") {
                key = ch;
            }
            if (key) {
                const subs = subscribers.get(key);
                if (subs) subs.forEach((cb) => { try { cb(data); } catch {} });
            }
        } catch {}
    };

    socket.onclose = () => {
        setTimeout(() => connect(), 1500);
    };
    socket.onerror = () => {
        try { socket.close(); } catch {}
    };

    return socket;
}

function parseKey(key) {
    if (key === "allMids") return { type: "allMids" };
    if (key.startsWith("candle:")) {
        const [, coin, interval] = key.split(":");
        return { type: "candle", coin, interval };
    }
    const [type, coin] = key.split(":");
    return coin ? { type, coin } : { type };
}

export function subscribe(sub, callback) {
    const key = getKey(sub);
    if (!subscribers.has(key)) {
        subscribers.set(key, new Set());
        const s = connect();
        const payload = JSON.stringify({ method: "subscribe", subscription: sub });
        if (s.readyState === 1) s.send(payload); else messageQueue.push(payload);
    } else {
        connect();
    }
    subscribers.get(key).add(callback);

    return () => {
        const set = subscribers.get(key);
        if (!set) return;
        set.delete(callback);
        if (set.size === 0) {
            subscribers.delete(key);
            if (socket && socket.readyState === 1) {
                socket.send(JSON.stringify({ method: "unsubscribe", subscription: sub }));
            }
        }
    };
}

export async function fetchInfo(body) {
    const r = await fetch("https://api.hyperliquid.xyz/info", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
    });
    if (!r.ok) throw new Error(`HL info ${r.status}`);
    return r.json();
}

export async function fetchCandles(coin, interval = "1m", lookbackMs = 1000 * 60 * 60 * 6) {
    const endTime = Date.now();
    const startTime = endTime - lookbackMs;
    return fetchInfo({
        type: "candleSnapshot",
        req: { coin, interval, startTime, endTime },
    });
}

export async function fetchMeta() {
    return fetchInfo({ type: "meta" });
}
